/**
 * seedAgentes.js — Seed del primer agente: Publicidad Mercado Libre
 *
 * Uso desde consola del browser:
 *   await window.__seedAgentes()
 *
 * O importar y llamar directamente.
 */

import { upsertAgente } from './agentes'

const AGENTES_SEED = [
  {
    id: 'agente-publicidad-ml',
    nombre: 'Publicidad Mercado Libre',
    descripcion: 'Analiza campañas de Product Ads y genera tareas de optimización: pausar campañas ineficientes, activar campañas con potencial, ajustar presupuestos.',
    tipo: 'plataforma', // Agente de Plataforma: corre clasificador y crea tareas automáticamente
    area: 'publicidad',
    dashboardId: 'ml-publicidad',
    // 11-jul-2026: IDs de sección REALES del clasificador (el gate del agente
    // compara agente.secciones con estos ids — las labels humanas lo rompían).
    secciones: [
      'activar',
      'pausar',
      'pausarRent',
      'quebreStock',
      'optimizar',
      'revisar',
      'potencial',
    ],
    instrucciones: `Este agente revisa las campañas de Product Ads de Mercado Libre y genera tareas para el consultor:

1. PAUSAR: Campañas con ACOS > 50% sostenido por más de 7 días y sin conversiones recientes.
2. ACTIVAR: Publicaciones con ventas orgánicas que no tienen campaña activa.
3. OPTIMIZAR: Campañas con ACOS entre 30-50% que podrían mejorar ajustando el presupuesto diario.
4. ESCALAR: Campañas con ACOS < 15% y CTR > 1% — incrementar presupuesto.
5. REVISAR: Campañas con inversión significativa pero 0 ventas en los últimos 14 días.

El agente NO modifica campañas directamente. Solo crea tareas para que el consultor revise y ejecute.`,
    activo: true,
    icono: 'Zap',
    modoTareas: 'agrupado',
    ia: {
      usaIA: false,
      modelo: null,
      prompt: null,
      temperatura: null,
    },
  },
  {
    id: 'agente-preguntas-ml',
    nombre: 'Preguntas y Respuestas Mercado Libre',
    descripcion: 'Detecta publicaciones donde las preguntas de la gente revelan qué falta en la ficha (hallazgos IA del KB: frases, bullets y atributos listos para sumar) y franjas horarias con tiempo de respuesta sobre el SLA.',
    tipo: 'plataforma',
    area: 'preguntas',
    dashboardId: 'ml-preguntas-respuestas',
    // 14-jul-2026: agente CANÓNICO — reemplaza al doc huérfano del bot original
    // (id autogenerado en /agentes; el seed de terminal lo migra con
    // --migrar-huerfanos). IDs de sección REALES del clasificador.
    secciones: [
      'mejorar_publicacion',
      'atender_horario',
    ],
    instrucciones: `Este agente corre el MISMO clasificador que la vista "🎯 Detección de tareas" del dashboard Preguntas y Respuestas (kbItems + dashPreguntasMetricas + publicaciones + config de empresa):

1. MEJORAR PUBLICACIÓN (mejorar_publicacion): publicaciones con hallazgos de IA en el KB de preguntas — frases sugeridas, bullets y atributos faltantes que las preguntas de la gente revelan y la ficha todavía no refleja. La tarea lleva ese contenido listo para pegar (tipo modificar_publicacion — fusiona por publicación con Opiniones y Postventa, chip "N fuentes").

2. ATENDER HORARIO (atender_horario): franjas horarias con tiempo promedio de respuesta por encima del SLA de la empresa (empresa.slaAtencion.ml.preguntasMin, default 60 min). Cubrir esas ventanas mejora la conversión pregunta→venta.

El agente NO responde preguntas ni modifica fichas. Solo crea tareas para que el consultor revise.`,
    activo: true,
    icono: 'MessageCircle',
    modoTareas: 'individual',  // 1 tarea por publicación / franja (como Opiniones)
    ia: {
      usaIA: false,
      modelo: null,
      prompt: null,
      temperatura: null,
    },
  },
  {
    id: 'agente-opiniones-ml',
    nombre: 'Opiniones de productos Mercado Libre',
    descripcion: 'Analiza las reviews por publicación y genera tareas para mejorar la ficha: destacar elogios recurrentes y contrarrestar quejas no abordadas.',
    tipo: 'plataforma',
    area: 'opiniones',
    dashboardId: 'ml-opiniones',
    secciones: [
      'mejorar_publicacion',
      'atender_quejas_recurrentes',
    ],
    instrucciones: `Este agente revisa las opiniones de productos sincronizadas (kbOpinionesItems) y genera tareas para el consultor:

1. MEJORAR PUBLICACIÓN: cada publicación con clusters de elogios o quejas que NO están reflejados en la ficha.
   - Elogios recurrentes (positivos) → frase para destacar en la descripción.
   - Quejas recurrentes (negativos) → refutación / contexto a sumar a la descripción para evitar reviews negativas futuras de gente que esperaba otra cosa.
   - Bullets, atributos faltantes y preguntas previstas se incluyen como sub-hallazgos.

2. ATENDER QUEJAS RECURRENTES: publicaciones con rating bajo (≤3) y volumen suficiente (≥3 reviews) que requieren acción más allá del copy: revisar producto, packaging, instrucciones, etc.

El agente NO modifica fichas. Solo crea tareas para que el consultor revise.`,
    activo: true,
    icono: 'Star',
    modoTareas: 'individual',  // 1 tarea por publicación
    ia: {
      usaIA: false,
      modelo: null,
      prompt: null,
      temperatura: null,
    },
  },
  {
    id: 'agente-postventa-ml',
    nombre: 'Analista de Postventa Mercado Libre',
    descripcion: 'Analiza reclamos, devoluciones y reputación. Genera tareas estructurales (modificar/pausar publicación, cambiar transportista/proveedor/embalaje) basadas en reglas determinísticas con umbrales editables por sección.',
    tipo: 'plataforma',
    area: 'postventa',
    dashboardId: 'ml-postventa',
    secciones: [
      'modificar_publicacion',
      'pausar_publicacion',
      'cambiar_transportista',
      'cambiar_proveedor',
      'cambiar_embalaje',
      'otras_problematicas',
    ],
    instrucciones: `Este agente revisa los reclamos + devoluciones + órdenes (colecciones canónicas) y genera tareas determinísticas (sin IA) basadas en umbrales editables por sección. La lógica es: para cada sección, calcular el promedio de la cuenta y marcar como candidato a acción cualquier item que esté por encima del promedio × (1 + tolerancia/100). El operador valida y crea la tarea.

1. MODIFICAR PUBLICACIÓN (sin tolerancia, va siempre para aprendizaje):
   Incluye todas las publicaciones con ≥1 reclamo o devolución en la ventana, con volumen mínimo de ventas. Cubre tanto el "ajustar atributos" como el "actualizar descripción" — es la sección que junta todo lo de "modificar el item".

2. PAUSAR PUBLICACIÓN:
   Métrica: % de ventas con reclamo o devolución en la ventana.
   Item entra si tasa > promedio_cuenta × (1 + tolerancia/100) AND ventas >= volumen_minimo.

3. CAMBIAR TRANSPORTISTA:
   Métrica: % de envíos con quejas por transportista en una zona.
   Item entra si tasa > promedio_cuenta × (1 + tolerancia/100) AND envios >= volumen_minimo.

4. CAMBIAR PROVEEDOR:
   Métrica: % de devoluciones por defecto por producto.
   Item entra si tasa > promedio_cuenta × (1 + tolerancia/100) AND ventas >= volumen_minimo.

5. CAMBIAR EMBALAJE:
   Métrica: % de devoluciones con motivo "damaged" por item.
   Item entra si tasa > promedio_cuenta × (1 + tolerancia/100) AND devoluciones >= volumen_minimo.

El agente NO modifica nada en ML. Solo crea tareas para que el operador entre a Mercado Libre y ejecute manualmente. Tab Patrones es independiente — ahí sí hay análisis IA, lo maneja otro flujo.`,
    activo: true,
    icono: 'RotateCw',
    modoTareas: 'individual',  // 1 tarea por publicación / transportista / proveedor / item
    ia: {
      usaIA: false,
      modelo: null,
      prompt: null,
      temperatura: null,
    },
    // Umbrales editables por sección — el evaluador determinístico los lee desde acá.
    // Mariano puede ajustarlos por empresa desde la UI sin redeploy.
    umbrales: {
      modificar_publicacion: {
        // Entra siempre (sin tolerancia) — para aprendizaje continuo.
        volumen_minimo_ventas: 5,
        ventana_dias: 30,
      },
      pausar_publicacion: {
        tolerancia_pct: 20,
        volumen_minimo_ventas: 10,
        ventana_dias: 30,
      },
      cambiar_transportista: {
        tolerancia_pct: 20,
        volumen_minimo_envios: 20,
        ventana_dias: 30,
      },
      cambiar_proveedor: {
        tolerancia_pct: 20,
        volumen_minimo_ventas: 30,
        ventana_dias: 60,
      },
      cambiar_embalaje: {
        tolerancia_pct: 20,
        volumen_minimo_devoluciones: 5,
        ventana_dias: 60,
      },
    },
  },
  {
    id: 'agente-full-ml',
    nombre: 'Estrategia Full Mercado Libre',
    descripcion: 'Gestiona el ciclo de Full: enviar candidatos con ventas, reponer antes del quiebre, y la escalera de lentos — publicitar (sin tráfico), promocionar (con tráfico y espacio de rentabilidad) o retirar (ciclo ≥45 días pagando storage o palancas agotadas).',
    tipo: 'plataforma',
    area: 'estrategia-full',
    dashboardId: 'ml-descarga-full',
    secciones: ['enviar_full', 'reponer_full', 'publicitar_lento', 'promocionar_lento', 'retirar_full'],
    instrucciones: `Este agente corre el mismo clasificador que la vista "🎯 Detección de tareas" del dashboard Estrategia Full:

1. ENVIAR A FULL (enviar_full):
   Publicaciones activas con ventas 30d fuera de Full, consolidadas por MLAU. EXCLUYE rotación baja (<3 u/30d — mandar lentos a Full crea sobrestock). Orden: facturación DESC.

2. REPONER EN FULL (reponer_full):
   En Full sin stock o con quiebre proyectado antes del R total (R producto + días de envío + procesamiento, config por empresa). La tarea trae pedirMin/pedirOk.

La ESCALERA de lentos (ya pagan o van a pagar storage al día 60, proyección FIFO) — una sola acción por item:

3. RETIRAR DE FULL (retirar_full) — el deadline se chequea PRIMERO:
   Ciclo cumplido (edad del lote ≥ cicloRetiroDias, default 45, config por empresa) con stock que va a pagar storage, O palancas agotadas (ad + promo activos y sigue lento). Orden: costo de storage proyectado DESC.

4. PUBLICITAR LENTO (publicitar_lento):
   Lento SIN tráfico (visitas < piso, default 100) y sin ad → falta visibilidad (activar_ad).

5. PROMOCIONAR LENTO (promocionar_lento):
   Lento CON tráfico y sin promo, gateado por RENTABILIDAD (cascada ADR 032 producto→categoría→marca→proveedor→empresa): con espacio (margen > mínimo) se sugiere CON números (descuento máximo + precio piso); sin datos se sugiere con advertencia; sin espacio NO se sugiere (declarado) → activar_promo.

Reglas del agente:
- Dedupe por ítem (tipo__itemId) + cuota de producción: lotes según la cuota del tipo; no crea otro lote mientras haya tarea abierta del tipo.
- El agente NO toca Full ni ML. Solo crea tareas para que el operador ejecute.
- Modo de tareas: agrupado (1 tarea por sección con N publicaciones como recursos).`,
    activo: true,
    icono: 'Package',
    modoTareas: 'agrupado',
    ia: { usaIA: false, modelo: null, prompt: null, temperatura: null },
  },
  {
    id: 'agente-flex-ml',
    nombre: 'Estrategia Flex Mercado Libre',
    descripcion: 'Cruza las zonas Flex de Mercado Libre con las ventas y la capacidad del operador (⚙️ Operación por zona): activar zonas apagadas con ventas, ampliar el horario hasta el tope de cada zona, y revisar la performance cuando ML degrada la cuenta.',
    tipo: 'plataforma',
    area: 'estrategia-flex',
    dashboardId: 'ml-flex-zonas',
    secciones: ['activar_zona', 'ampliar_horario', 'revisar_performance'],
    instrucciones: `Este agente corre el mismo clasificador que la vista "🎯 Detección de tareas" del dashboard Estrategia Flex. Las ZONAS no se inventan: son las que trae Mercado Libre en el sync de Flex, y el item de cada tarea ES la zona.

1. ACTIVAR ZONA (activar_zona → activar_zona_flex):
   Zonas Flex APAGADAS con ventas en la ventana (≥ piso de órdenes, default 5). Activando Flex esas órdenes se entregan en el día. Orden: facturación DESC.

2. AMPLIAR HORARIO (ampliar_horario → ampliar_horario_flex):
   Zonas PRENDIDAS con órdenes creadas después del cutoff actual de ML y antes del TOPE DEL OPERADOR de esa zona (⚙️ Operación por zona; default 16:00 si no hay setup). Nunca sugiere más allá del tope — cada zona es independiente y su capacidad la define su operador. Si la zona tiene capacidadPaquetes y el promedio diario Flex ya la llena, no se sugiere (declarado).

3. REVISAR PERFORMANCE (revisar_performance → revisar_performance_flex):
   is_downgraded de ML activo → UNA tarea de gestión (esfuerzo 4, no perder, SLA 24h) con el estado de las suscripciones.

Reglas del agente:
- Dedupe por ítem (tipo__zonaId): una zona con tarea abierta del mismo tipo no se vuelve a proponer.
- El agente NO toca la configuración de Flex en ML. Solo crea tareas para que el operador ejecute.
- Modo de tareas: agrupado (1 tarea por sección con N zonas como recursos).`,
    activo: true,
    icono: 'MapPin',
    modoTareas: 'agrupado',
    ia: { usaIA: false, modelo: null, prompt: null, temperatura: null },
  },
  {
    id: 'agente-central-promos-ml',
    nombre: 'Central de Promociones Mercado Libre',
    descripcion: 'Corre el motor de decisión de la Central de Promociones (margen post-promo vs Reglas de Decisión, perfil histórico de venta, ACOS vs empresa, stock) sobre cada par ítem×campaña: sumarse, promo propia, sumarse apagando publi (mitad 1), apagar publi en promo y bajarse.',
    tipo: 'plataforma',
    area: 'central-promociones',
    dashboardId: 'ml-central-promociones',
    secciones: [
      'sumarse_promo',
      'promo_propia',
      'sumarse_apagando_publi',
      'apagar_publi_promo',
      'bajarse_promo',
    ],
    instrucciones: `Este agente corre el mismo clasificador que la vista "🎯 Detección de tareas" de la Central de Promociones (motor calcularAccionPromo — el de la vista "Por acción"). El umbral OK es el más exigente entre el piso absoluto y el relativo al neto de la empresa (Reglas de Decisión central-promociones, cascada empresa → global → defaults).

1. SUMARSE (sumarse_promo → activar_promo):
   Candidatas donde el margen post-promo NETO (margen sin ads − descuento seller − ACOS actual) queda ≥ umbral. Conviene entrar. Si el ítem es candidato en varias campañas, UNA tarea con la más conveniente (las otras quedan listadas en las métricas).

2. PROMO PROPIA (promo_propia → activar_promo):
   Publicaciones activas SIN promo de ML cuyo margen aguanta el descuento default de Reglas de Decisión. Bajar el precio directo desde la publicación.

3. SUMARSE APAGANDO PUBLI (sumarse_apagando_publi → pausar_ad) — ACCIÓN DOBLE, MITAD 1:
   La publi es cara (ACOS > coef × ACOS empresa) o redundante (histórico: vende con descuento sin publi) pero el descuento cierra. NO se emite tarea compuesta: se emite SOLO pausar_ad. Cuando la publi queda apagada, la PRÓXIMA corrida evalúa el ítem sin ACOS → cae en "Sumarse" → sale el activar_promo (mitad 2). El pipeline vive en la re-detección; el estado intermedio (publi apagada, sin promo) es el seguro. Si el mismo ítem ya cae en "apagar publi en promo", esa tarea cubre la mitad 1 (no se duplica).

4. APAGAR PUBLI EN PROMO (apagar_publi_promo → pausar_ad):
   Participando: sin publi la promo cierra (≥ umbral), con publi queda abajo. Apagar la publicidad mientras dure la promo.

5. BAJARSE (bajarse_promo → pausar_promo):
   Participando y no cierra ni apagando la publi (margen sin publi < umbral o negativo), o stock crítico. Bajarse para no quemar margen/saldo. (Reponer se gestiona desde Velocidad de Consumo.)

Declarados sin tarea: mantener, no sumarse, sin stock, sin datos y publicaciones pausadas (ML deja precios viejos — datos no accionables).

Reglas del agente:
- Dedupe por ítem (tipo__itemId) + cuota de producción del tipo (override empresa → catálogo).
- SIN gate genérico de rentabilidad: el motor ya decide por margen con las Reglas de Decisión (más fino).
- El agente NO toca promos ni publicidad en ML. Solo crea tareas para que el operador ejecute.
- Modo de tareas: agrupado (1 tarea por sección con N publicaciones como recursos).`,
    activo: true,
    icono: 'Tags',
    modoTareas: 'agrupado',
    ia: { usaIA: false, modelo: null, prompt: null, temperatura: null },
  },
  {
    id: 'agente-tendencias-ml',
    nombre: 'Tendencias de Demanda Mercado Libre',
    descripcion: 'Cruza las keywords en tendencia de ML con tus publicaciones: reponer stock donde la búsqueda exacta está caliente, revisar buy-box en tendencia, y optimizar títulos SOLO con pares verificados por el gate de compatibilidad IA (marca ajena y atributos que no aplican se vetan).',
    tipo: 'plataforma',
    area: 'tendencias',
    dashboardId: 'ml-tendencias',
    secciones: ['reponer_tendencia', 'buybox_tendencia', 'titulo_amplia', 'titulo_sin'],
    instrucciones: `Este agente corre el mismo clasificador que la vista "🎯 Detección de tareas" del dashboard Tendencias de demanda (keywords de refTendencias vía resolverTendencias, cache-first con TTL semanal).

1. REPONER (reponer_tendencia → reponer_stock):
   El título contiene la búsqueda EXACTA en tendencia y la publicación está sin stock — demanda de hoy sin atender. Orden: vendidas DESC. Sin gate IA (no se sugiere agregar nada al título).

2. BUY-BOX (buybox_tendencia → revisar_competitividad):
   Búsqueda exacta en tendencia y perdiendo la caja en catálogo. Revisar precio y palancas del ganador.

3. OPTIMIZAR TÍTULO amplia → exacta (titulo_amplia → modificar_publicacion):
   Vendés algo relacionado (sin ventas) — meter la keyword que falta pasa el título a concordancia exacta.

4. OPTIMIZAR TÍTULO sin concordancia (titulo_sin → modificar_publicacion):
   Sin ventas y el título no agarra ninguna búsqueda del rubro — sumar una keyword sugerida. Backlog: comparte tipo y cuota con la sección anterior.

GATE DE COMPATIBILIDAD (decisión Mariano 12-jul-2026) — aplica a las secciones de TÍTULO:
- El agente SOLO crea tareas de pares (título × keyword) VERIFICADOS: veredicto IA aplica=true (cache tendenciasVeredictosTitulo, callable sugerirTendenciaIA tipo 'compatibilidad') o keyword con marca PROPIA del catálogo (determinístico).
- aplica=false → vetada: marca ajena (ej. "cafe martinez") o atributo que el producto no tiene (ej. "corpiño lactancia"). Nunca se sugiere.
- Sin veredicto → pendiente (declarado): se verifica desde la vista con "Verificar con IA". El agente NO llama a la IA por su cuenta.
- keywordsExcluidas (empresa.tendenciasConfig) = curación manual: lo excluido no existe para el motor.
- Gap de producto (keywords país sin cobertura) queda DECLARADO — decisión de sourcing, no tarea.

Reglas del agente:
- Dedupe por ítem (tipo__itemId) + cuota de producción del tipo. reponer_stock y revisar_competitividad se cruzan con Velocidad/Catálogo (gana el agente que corre primero).
- El agente NO modifica títulos ni nada en ML. Solo crea tareas para que el operador ejecute.
- Modo de tareas: agrupado (1 tarea por sección con N publicaciones como recursos).`,
    activo: true,
    icono: 'TrendingUp',
    modoTareas: 'agrupado',
    ia: { usaIA: false, modelo: null, prompt: null, temperatura: null },
  },
  {
    id: 'agente-evolucion-matriz-ml',
    nombre: 'Evolución de la Matriz Mercado Libre',
    descripcion: 'Mira la película (serie mensual de cuadrantes, meses completos) y detecta trayectorias: caídas para revisar qué cambió, crónicas con capital atrapado para revisar de fondo (o discontinuar), y despegues sin publicidad para acelerar con un ad.',
    tipo: 'plataforma',
    area: 'evolucion-matriz',
    dashboardId: 'ml-evolucion-matriz',
    secciones: ['caida_matriz', 'cronica_matriz', 'despertar_matriz'],
    instrucciones: `Este agente corre el mismo clasificador que la vista "🎯 Detección de tareas" de Evolución de la Matriz. Mira la PELÍCULA (serie mensual de cuadrantes con los mismos umbrales 1.15/0.85 de la Matriz, métrica unidades, meses COMPLETOS — el mes en curso parcial no cuenta para no fabricar falsas caídas).

1. EN CAÍDA (caida_matriz → revisar_competitividad, prioridad alta):
   Venía bien (estrella/moderada) y lleva meses cayendo de cuadrante. La tarea trae la trayectoria y la facturación perdida. Investigar qué cambió: precio vs competencia, buy-box, publi, promo vencida. EXCLUYE caídas con stock 0 (sospecha de quiebre — la causa es stock, no competitividad: Velocidad ya pide reponer; quedan declaradas). Orden: facturación perdida DESC.

2. CRÓNICA (cronica_matriz → revisar_competitividad, prioridad baja):
   Eventual/incógnita TODOS los meses del período, sin asomar nunca. Es backlog: revisar precio/listing vs competencia de fondo; si no hay arreglo, la conclusión de la tarea es discontinuar o liquidar. Orden: capital atrapado (stock × precio) DESC.

3. DESPERTANDO (despertar_matriz → activar_ad, prioridad media):
   Venía baja y está subiendo sola — acelerarla con publicidad. Solo pubs SIN ad inscripto (cualquier estado, mismo criterio que la Matriz); con ad quedan declaradas.

Declaradas sin tarea: ⭐ core (no se toca) · 🔥 recuperando (ya vuelve sola) · 🔄 volátil (impredecible) · ⏸ estable · sin ventas en todo el período (ese universo lo trabaja la Matriz simple) · estrella con margen negativo (dominio de Contribución Neta).

Reglas del agente:
- caida y cronica comparten tipo (revisar_competitividad) → comparten cuota y dedupe: las caídas drenan primero, las crónicas esperan. Cruces: si Catálogo/Publicidad ya pidieron revisar esa pub, no se duplica; activar_ad se cruza con Matriz/Velocidad/Full.
- El agente NO modifica nada en ML. Solo crea tareas para que el operador ejecute.
- Modo de tareas: agrupado (1 tarea por sección con N publicaciones como recursos).`,
    activo: true,
    icono: 'LineChart',
    modoTareas: 'agrupado',
    ia: { usaIA: false, modelo: null, prompt: null, temperatura: null },
  },
  {
    id: 'agente-velocidad-consumo-ml',
    nombre: 'Velocidad de Consumo Mercado Libre',
    descripcion: 'Analiza la rotación de stock por publicación (velocidad de venta, días hasta quiebre, cobertura) y genera tareas: reponer stock urgente, reponer stock por cobertura, activar promoción (sobradas con tráfico) y activar publicidad (sobradas sin tráfico) — en lotes según la cuota de producción.',
    tipo: 'plataforma',
    area: 'velocidad-consumo',
    dashboardId: 'ml-velocidad-consumo',
    secciones: [
      'reponer_quiebre',
      'reponer_velocidad',
      'activar_promo_sobrado',
      'activar_ad_sobrado',
    ],
    instrucciones: `Este agente corre el mismo clasificador que la vista "🎯 Detección de tareas" del dashboard Velocidad de Consumo (split limpio 12-jul):

1. REPOSICIÓN URGENTE (reponer_quiebre):
   Publicaciones con urgencia = "quebrado" (stock = 0). Generar tarea para reponer URGENTE.

2. REPONER STOCK (reponer_velocidad):
   Publicaciones con urgencia "critico" o "bajo" (cobertura por días-a-quiebre debajo del umbral). Van a quebrar pronto.

3. ACTIVAR PROMOCIÓN (activar_promo_sobrado):
   Sobrestockeadas con ≥1000 visitas/30d y SIN promo activa — el tráfico está, falta el incentivo (descuento).

4. ACTIVAR PUBLICIDAD (activar_ad_sobrado):
   Sobrestockeadas con <1000 visitas/30d y SIN ad activo — les falta visibilidad antes que descuento.

Reglas del agente:
- Dedupe por ítem (tipo__itemId) + cuota de producción: genera lotes según la cuota del tipo (override por empresa → default del catálogo) y no crea otro lote mientras haya una tarea abierta del mismo tipo.
- El agente NO modifica nada en ML. Solo crea tareas para que el operador entre a Mercado Libre y ejecute.
- Modo de tareas: agrupado (1 tarea por sección con N publicaciones como recursos). El operador resuelve individualmente desde la checklist.`,
    activo: true,
    icono: 'Gauge',
    modoTareas: 'agrupado',
    ia: {
      usaIA: false,
      modelo: null,
      prompt: null,
      temperatura: null,
    },
    umbrales: {
      diasCubrir: 30,
      umbralVisitasPromoVsAd: 1000,
    },
  },
  {
    id: 'agente-contribucion-neta-ml',
    nombre: 'Análisis de Contribución Neta Mercado Libre',
    descripcion: 'Detecta publicaciones que venden a pérdida y sugiere el precio al que hay que venderlas para ganar el margen mínimo aceptado (de la ficha del producto), manteniendo el descuento y la publicidad observados. Pausar ad/promo viven en Publicidad y Central de Promociones.',
    tipo: 'plataforma',
    area: 'rentabilidad',
    dashboardId: 'ml-contribucion-neta',
    // 12-jul-2026 (rework acción única): el agente auto-crea SOLO subir_precio
    // (pérdida). mejorar_margen (bajo el mínimo, sin pérdida) queda para
    // creación manual desde la vista 🎯. Cada tipo tiene su cuota.
    secciones: [
      'subir_precio',
    ],
    instrucciones: `Este agente revisa las órdenes canónicas (colección ordenes) con rentabilidad calculada (cascada del dashboard de Contribución Neta) y genera tareas determinísticas (sin IA) para cada publicación con al menos una orden de contribución negativa en la ventana.

ANTES DE CLASIFICAR — persistencia automática de rentabilidad:
   El ejecutor dispara automáticamente \`calcularRentabilidadEmpresa({ modo: 'incremental' })\` antes del clasificador para garantizar que todas las órdenes en la ventana tengan _rentabilidad persistido. Eso elimina el mismatch entre dashboard (que hidrata en runtime) y agente (que lee de Firestore).

LÓGICA DE DECISIÓN (por orden negativa concreta — peor caso):

1. COOLDOWN BITÁCORA 30d:
   Si en últimos 30d hay eventos ad_desactivado Y promo_desactivada para esa publicación (a nivel producto en productoBitacora), no se recomienda pausar otra vez. Se va directo a SUBIR PRECIO.
   Cooldown parcial: si solo uno de los dos eventos está en ventana, ese tipo no se vuelve a recomendar; el otro sí puede.

2. PÉRDIDA POR COSTOS FIJOS:
   Si TODAS las órdenes negativas tienen costoPromo=0 Y costoAd=0, la pérdida es por costos fijos (comisión, envío, impuestos, costo producto). No hay nada que pausar — SUBIR PRECIO al netoPctProm de la cuenta.

3. ALGUNA ORDEN TUVO COSTOPROMO O COSTOAD:
   Por cada orden negativa, comparar costoPromo (full_unit_price × qty − subtotal) vs costoAd (_rentabilidad.publicidad prorrateado por línea).
   - Si promo > ad → pausar_promo (siempre). Simular: si (rentaNeta + costoPromo) < 0 y hubo costoAd → también pausar_ad.
   - Si ad ≥ promo y publicidad > 0 → pausar_ad. Simular: si (rentaNeta + publicidad) < 0 y hubo costoPromo → también pausar_promo.
   NO filtramos por estado actual de la pub (tieneAdHoy/tienePromoHoy): la tarea "Pausar X" implica "revisar X y pausar si está activa, o asegurarse de no inscribirla a otras". Si la palanca ya no está activa, el operador cierra con nota.

PRECIO SUGERIDO en subir_precio:
   precio = costoUnit / (1 − netoPctProm de la cuenta). Modelo simplificado que asume que comisión/envío/impuestos escalan proporcional al precio (misma fórmula conceptual que Velocidad de Consumo). El operador valida en la tarea.

DEDUP CROSS-AGENTE (mayo 2026):
   El ejecutor busca tareas activas con mismo tipoAccion (pausar_ad, pausar_promo, subir_precio) — independiente del agente que las creó (Velocidad Consumo, manual, otro). Si una pub ya tiene tarea activa con ese tipoAccion, se FILTRA del recurso de la nueva tarea (no se duplica). Si TODAS las pubs candidatas de la sección ya tienen tarea, la sección se omite entera.

MODO DE TAREAS: agrupado (1 tarea por sección con N publicaciones como recursos). El operador resuelve individualmente desde la checklist; cada recurso lleva órdenes negativas como evidencia.`,
    activo: true,
    icono: 'Wallet',
    modoTareas: 'agrupado',
    ia: {
      usaIA: false,
      modelo: null,
      prompt: null,
      temperatura: null,
    },
    umbrales: {
      cooldownDiasBitacora: 30,
    },
  },
  {
    id: 'agente-publicaciones-ml',
    nombre: 'Publicaciones Mercado Libre',
    descripcion: 'Detecta oportunidades de mejora en las publicaciones: cuotas disponibles sin activar, calidad (score de salud) bajo el umbral y fotos principales mejorables — priorizadas por tráfico y generadas en lotes según la cuota de producción.',
    tipo: 'plataforma',
    area: 'publicaciones',
    dashboardId: 'ml-publicaciones',
    secciones: [
      'agregar_cuotas',
      'mejorar_calidad',
      'modificar_foto',
      'modificar_foto_sin_trafico',
    ],
    instrucciones: `Este agente corre el mismo clasificador que la vista "🎯 Detección de tareas" del dashboard Publicaciones:

1. AGREGAR CUOTAS (agregar_cuotas): productos (MLAU) sin ninguna cuota activa cuya categoría sí permite cuotas (universo campanasCuotas).
2. MEJORAR CALIDAD (mejorar_calidad): score de salud (performance ML) por debajo del umbral (default 80, configurable por empresa).
3. MEJORAR FOTO — CON TRÁFICO (modificar_foto): foto principal sin good_quality_picture en publicaciones con visitas en 30d, ordenadas de mayor a menor tráfico. Quick win.
4. MEJORAR FOTO — SIN TRÁFICO (modificar_foto_sin_trafico): el backlog sin visitas, prioridad baja.

Las secciones de foto comparten el tipo modificar_foto y su CUOTA DE PRODUCCIÓN: el agente genera lotes (default 20, override por empresa) y no crea otro lote mientras haya una tarea abierta del tipo.

El agente NO modifica publicaciones. Solo crea tareas para que el consultor revise y ejecute.`,
    activo: true,
    icono: 'Image',
    modoTareas: 'agrupado',
    ia: {
      usaIA: false,
      modelo: null,
      prompt: null,
      temperatura: null,
    },
  },
  {
    id: 'agente-catalogo-ml',
    nombre: 'Catálogo Mercado Libre',
    descripcion: 'Lee el advisor de buy-box y genera tareas: bajar precio donde ganar conviene (margen sobre el corte), revisar competitividad al límite, y palancas donde el precio no alcanza.',
    tipo: 'plataforma',
    area: 'catalogo',
    dashboardId: 'ml-catalogo',
    secciones: [
      'bajar_precio_ganar',
      'revisar_al_limite',
      'revisar_palancas',
    ],
    instrucciones: `Este agente lee las sublistas precalculadas del advisor de buy-box (no recalcula el motor) y genera tareas:

1. BAJAR PRECIO PARA GANAR (bajar_precio_ganar): estado "conviene" — bajar al precio-para-ganar deja margen ARRIBA del corte. Prioridad alta, ordenado por Δ vs corte (las más seguras primero).
2. REVISAR AL LÍMITE (revisar_al_limite): estado "al límite" — ganar deja margen positivo pero bajo el corte; el consultor compara y decide. Prioridad media, las más cerca de convenir primero.
3. PALANCAS (revisar_palancas): estado "no conviene" — ganar por precio da pérdida; la jugada son las palancas del ganador (Full, cuotas, envío) o el costo. Prioridad BAJA (backlog marginal).

No generan tarea: ya ganás, no competible, no aplica, sin vincular (para eso está Sembrar) y pendiente.

El agente NO modifica precios. Solo crea tareas con el veredicto completo del advisor para que el consultor decida.`,
    activo: true,
    icono: 'BookOpen',
    modoTareas: 'agrupado',
    ia: {
      usaIA: false,
      modelo: null,
      prompt: null,
      temperatura: null,
    },
  },
  {
    id: 'agente-matriz-ml',
    nombre: 'Matriz de Distribución Mercado Libre',
    descripcion: 'Cruza cuadrantes de la matriz con tráfico: promocionar tráfico sin venta y publicitar venta sin tráfico.',
    tipo: 'plataforma',
    area: 'matriz-distribucion',
    dashboardId: 'ml-matriz-distribucion',
    secciones: [
      'promocionar',
      'publicitar',
    ],
    instrucciones: `Este agente corre el mismo clasificador que la vista "🎯 Detección de tareas" de la Matriz (ventana 30 días, métrica unidades, umbrales 1.15/0.85 espejo del dashboard):

1. PROMOCIONAR (promocionar): incógnitas (0 ventas) con visitas por encima del piso (default 100, configurable por empresa) y SIN promoción activa → activar_promo.
2. PUBLICITAR (publicitar): sin ventas, sin tráfico (visitas bajo el piso), sin promo, sin publicidad y CON stock (sin stock no figura) — el rincón muerto: la publicidad les trae el tráfico inicial → activar_ad. Orden: precio DESC (ticket más alto primero); la cuota de producción del tipo (50) lo drena en lotes.
Las publicaciones que VENDEN no generan tarea en la Matriz (se declaran). El agente NO modifica promos ni ads: solo crea tareas.`,
    activo: true,
    icono: 'Grid3x3',
    modoTareas: 'agrupado',
    ia: {
      usaIA: false,
      modelo: null,
      prompt: null,
      temperatura: null,
    },
  },
  {
    id: 'agente-trafico-conversion-ml',
    nombre: 'Tráfico y Conversión Mercado Libre',
    descripcion: 'Cruza tráfico (visitas 30d) con conversión (ventas facturables): potenciar publicaciones con CVR alto sin ads, visibilizar activas sin tráfico y revisar competitividad donde hay mucho tráfico que convierte mal.',
    tipo: 'plataforma',
    area: 'trafico-conversion',
    dashboardId: 'ml-trafico-conversion',
    secciones: [
      'potenciar',
      'sin_trafico',
      'revisar_cvr',
    ],
    instrucciones: `Este agente corre el MISMO clasificador que la vista "🎯 Detección de tareas" del dashboard Tráfico y Conversión (ventana 30 días, CVR = ventas facturables / visitas, medianas espejo del dashboard):

1. POTENCIAR (potenciar): publicaciones ACTIVAS sin publicidad con CVR por encima de la mediana de las que convierten (venta > 0). Convierten bien pero les falta tráfico -> crear_ad para traerles pauta.
2. SIN TRÁFICO (sin_trafico): activas, sin publicidad y sin visitas en el período (invisibles) -> crear_ad para visibilizarlas.
3. CVR BAJO (revisar_cvr): visitas por encima de la mediana pero CVR por debajo (mucho tráfico que convierte mal) -> revisar_competitividad (precio, ficha, condiciones).

Las medianas se calculan sobre el universo de la cuenta (CVR sobre las que venden, visitas sobre las que tienen tráfico). El agente NO crea ads ni cambia precios: solo crea tareas para que el consultor revise y ejecute.`,
    activo: true,
    icono: 'TrendingUp',
    modoTareas: 'agrupado',
    ia: {
      usaIA: false,
      modelo: null,
      prompt: null,
      temperatura: null,
    },
  },
  {
    // Agente 16 — dashboard VIOLETA (15-jul-2026). Universo DISJUNTO de
    // Contribución Neta: acá SOLO publicaciones SIN venta en la ventana.
    id: 'agente-rentabilidad-inferida-ml',
    nombre: 'Rentabilidad Inferida Mercado Libre',
    descripcion: 'Para lo que todavía no vende: infiere el margen de cada publicación con la fórmula completa (comisión/envío por cascada cuenta → ecosistema + impuestos) y genera tareas de subir precio al precio sugerido — a pérdida con urgencia alta, bajo la mínima con urgencia media.',
    tipo: 'plataforma',
    area: 'rentabilidad-inferida',
    dashboardId: 'ml-rentabilidad-inferida',
    secciones: [
      'perdida_inferida',
      'bajo_minima_inferida',
    ],
    instrucciones: `Este agente corre el mismo clasificador que el dashboard "Rentabilidad inferida" (ml-rentabilidad-inferida). Universo: publicaciones ACTIVAS SIN venta en 30 días — las que SÍ vendieron las trabaja Contribución Neta (universos disjuntos, sin doble tarea). El margen se INFIERE con la fórmula completa de las órdenes reales: comisión/envío por cascada cuenta → ecosistema (Conocimiento) + impuestos de la config fiscal. Fuente declarada siempre; SIN costo cargado en el producto vinculado NO se infiere nada (no se inventa — esas quedan declaradas para sembrar/cargar costo).

Las DOS secciones emiten la MISMA acción — subir_precio al PRECIO SUGERIDO — en dos grupos para entender la urgencia (decisión Mariano 15-jul):

1. A PÉRDIDA (perdida_inferida → subir_precio, prioridad ALTA): margen inferido NEGATIVO — si vende al precio actual, pierde plata. Orden: capital en riesgo (stock × costo) DESC.
2. BAJO LA MÍNIMA (bajo_minima_inferida → subir_precio, prioridad MEDIA): margen positivo pero debajo de la rentabilidad mínima (cascada producto → categoría → marca → proveedor → empresa), con HISTÉRESIS de 3 puntos — una pub que oscila alrededor del corte no entra y sale de la sección en cada corrida (la zona gris queda declarada).

El PRECIO SUGERIDO de la tarea es el mínimo con el que la renta FINAL — aplicando el descuento y ACOS ESPERADOS (benchmarks cuenta → ecosistema), las palancas que seguramente se prendan para que venda — llega a la rentabilidad mínima (o al equilibrio si no hay mínima). Si ni cuadruplicando el precio se alcanza, la tarea lo declara: revisar el costo cargado o discontinuar.

Reglas del agente:
- Ads gastando sin vender: declarado por ítem y SIN ACOS esperado proyectado — la señal es pausar el ad (lo trabaja Publicidad, capa 1 del flywheel), no tocar el precio todavía.
- subir_precio es tipo LADO PLATA: dedupe y cruces con Contribución Neta comparten código pero los universos son disjuntos por diseño.
- El agente NO modifica precios en ML. Solo crea tareas para que el operador ejecute.
- Modo de tareas: agrupado (1 tarea por sección con N publicaciones como recursos).`,
    activo: true,
    icono: 'FlaskConical',
    modoTareas: 'agrupado',
    ia: {
      usaIA: false,
      modelo: null,
      prompt: null,
      temperatura: null,
    },
    umbrales: {
      histeresisPts: 0.03,
    },
  },
  {
    // Agente 17 — el INSUMO del flywheel (16-jul-2026, pedido Mariano):
    // "la tarea uno, la más importante". Capa 0 en flywheelCapas → en el
    // agente general corre en la PRIMERA ola, justo después del auto-seed
    // (paso 0 del general, que le alimenta el universo con los productos
    // recién sembrados). No es un dashboard: la vista manual es el banner
    // de Empresa → Productos (misma sección, mismo dedupe §4.5 por productoId).
    id: 'agente-productos-cargar-datos',
    nombre: 'Cargar Datos del Catálogo',
    descripcion: 'Detecta productos activos con vínculo ML sin costo y/o tiempo de reposición y crea la PRIMERA tarea de la cascada: completar los datos, con el CSV pre-llenado adjunto en la tarea. Sin costo no hay análisis de rentabilidad — es el insumo de todo el flywheel.',
    tipo: 'plataforma',
    area: 'productos',
    dashboardId: 'productos-cargar-datos',
    secciones: ['productos_sin_datos'],
    instrucciones: `Este agente corre el clasificador productos-cargar-datos (mismo criterio que el banner de Empresa → Productos — paridad vista↔agente):

UNIVERSO: productos ACTIVOS del catálogo con vínculo a una publicación de Mercado Libre (el CSV importa por link MLA — sin vínculo no hay cómo matchear) a los que les falta el COSTO (costoPromedio > 0) y/o el TIEMPO DE REPOSICIÓN (tiempoReposicionDias). La rentabilidad mínima NO dispara la detección (cascadea por categoría/marca/proveedor/empresa) pero viaja en el CSV como columna opcional.

1. CARGAR DATOS (productos_sin_datos → completar_datos_transaccionales): UNA tarea agrupada con los productos incompletos como recursos y el CSV pre-llenado ADJUNTO (título + link MLA + mínima actual; costo y reposición vacíos para completar). El operador lo completa y lo importa en Empresa → Productos → "Importar costos CSV" (matchea por link MLA, nunca pisa costos validados sin re-importar).

ORDEN de los recursos: falta COSTO primero (el insumo duro — sin costo no se infiere rentabilidad), después precio de venta DESC (el capital más grande primero).

Reglas del agente:
- Es CAPA 0 del flywheel: en la corrida general corre en la primera ola — cargar los datos es el prerequisito de Rentabilidad Inferida, Contribución Neta y todos los gates de la cascada.
- El AUTO-SEED (paso 0 del agente general) corre justo antes: siembra las publicaciones con actividad sin vínculo, y los productos recién creados (sin costo) entran a ESTA tarea en la misma corrida.
- Dedupe §4.5 por productoId (tipo completar_datos_transaccionales) — cruza con el flujo manual del banner de Productos: lo que ya está en una tarea abierta no se repite; solo entran productos nuevos.
- El agente NO modifica productos ni toca ML. Solo crea la tarea para que el operador complete e importe.
- Modo de tareas: agrupado (1 tarea con N productos como recursos + CSV adjunto).`,
    activo: true,
    icono: 'ClipboardList',
    modoTareas: 'agrupado',
    ia: { usaIA: false, modelo: null, prompt: null, temperatura: null },
  },
  {
    // ═══ AGENTE GENERAL de Detección (16-jul-2026) — el ORQUESTADOR ═══
    // No tiene dashboardId ni clasificador propio (esGeneral: true — el censo
    // lo ignora por diseño: solo censa agentes con dashboardId). Una corrida
    // = los agentes de detección activos de la empresa, ejecutados en el
    // orden de la CASCADA DEL FLYWHEEL (capa 0 Stock → 5 Cumplimiento, ver
    // src/core/lib/flywheelCapas.js) con CARGA ÚNICA (cache compartido de
    // colecciones — todos ven la misma foto) y reporte por corrida en
    // empresas/{id}/corridasDeteccion/{runId}. Motor: src/core/lib/agenteGeneral.js.
    id: 'agente-general-deteccion-ml',
    nombre: 'Detección General de Tareas',
    descripcion: 'Corre TODOS los agentes de detección de la empresa en el orden de la cascada del flywheel (Stock → Rentabilidad → Competitividad → Visibilidad → Conversión → Cumplimiento): la capa más cercana a la plata emite primero y veta a las de arriba. Carga única de datos y reporte completo de la corrida.',
    tipo: 'plataforma',
    area: 'general',
    dashboardId: null,
    esGeneral: true,
    secciones: [],
    instrucciones: `Este agente es el ORQUESTADOR de la flota de detección. Una corrida por empresa:

0. 🌱 AUTO-SEED (16-jul-2026, antes de todo): siembra en el catálogo las publicaciones CON ACTIVIDAD sin producto vinculado (activas con venta 30d o visitas; pausadas solo con venta 60d) vía el callable idempotente. Corre ANTES de la carga única a propósito: los productos recién creados (sin costo) entran en la MISMA corrida al agente de cargar-datos. Falla suave: se declara en el reporte y la corrida sigue.
1. ORDEN = la cascada del flywheel (docs/flywheel-mercadolibre.md): los agentes se ejecutan de la capa 0 (Stock + el insumo Cargar Datos del Catálogo) a la 5 (Cumplimiento), cada uno con su pipeline completo de siempre (clasificador + gates + cuarentena + dedupe + cuotas — paridad total con la corrida individual y con la vista 🎯).
2. VETO ENTRE CAPAS: la capa baja crea sus tareas primero; cuando una capa alta quiere la acción OPUESTA sobre el mismo ítem (activar_ad↔pausar_ad, activar_promo↔pausar_promo, subir_precio↔bajar_precio), el dedupe de opuestos la filtra — el lado que emitió la capa más baja manda. Los bloqueos quedan declarados en el reporte.
3. CARGA ÚNICA: las colecciones se leen una sola vez por corrida (cache compartido) — todos los agentes ven la MISMA foto de datos.
4. REPORTE: empresas/{id}/corridasDeteccion/{runId} — auto-seed + por capa y por agente: tareas creadas, secciones con items, omisiones, bloqueos declarados, errores y duración. El número siempre cierra.

El agente general NO clasifica nada por sí mismo y NO modifica nada en ML — siembra el catálogo (paso 0), orquesta a la flota y deja el libro de la corrida.`,
    activo: true,
    icono: 'Network',
    modoTareas: 'agrupado',
    ia: { usaIA: false, modelo: null, prompt: null, temperatura: null },
  },
]

export async function seedAgentes() {
  const results = []
  for (const agente of AGENTES_SEED) {
    const { id, ...data } = agente
    const resultId = await upsertAgente(id, data)
    results.push({ id: resultId, nombre: agente.nombre })
    console.log(`[seedAgentes] ✅ ${agente.nombre} (${resultId})`)
  }
  return results
}

// Exponer en window para uso desde consola
if (typeof window !== 'undefined') {
  window.__seedAgentes = seedAgentes
}
